import { deepEquals } from 'app/infrastructure/js';
import { linkCodesAction } from '../constants';
import { ListAction, ListBranch, ListBranchItem } from '../types-store';

export interface ItemSelectionState {
  path: number[];
  selected: boolean;
  childrenLoaded: boolean;
}

export const mapInitialSelection = (
  listBranch: ListBranch[],
  initialValueSelected: (item: ListBranchItem) => boolean,
  parentPath: number[] = undefined
): ItemSelectionState[] => {

  const parentId = parentPath?.[parentPath.length - 1] ?? 0;

  const items = listBranch
    ?.find(branch => branch.id === parentId)
    ?.items;

  if (!items?.length) {
    return [];
  }

  return items.reduce((result, item) => {

    const id = item.id;
    const selected = initialValueSelected?.(item) ?? false;
    const path = [...(parentPath ?? []), id];
    const childBranch = listBranch.find(branch => branch.id === item.id);
    const childrenLoaded = item.childrenCount === 0 || (!!childBranch &&
      childBranch.numberOfResults === childBranch.items.length);

    const children = mapInitialSelection(listBranch, initialValueSelected, path);

    return [
      ...result,
      {
        path,
        selected,
        childrenLoaded
      },
      ...(!!children && children)
    ];
  }, [] as ItemSelectionState[]);
};

const applySelectAll = (path: number[], propagate: boolean, selection: ItemSelectionState[]): ItemSelectionState[] =>
  selection.map(item => ({
    ...item,
    selected: true
  }));

const applyDeselectAll = (path: number[], propagate: boolean, selection: ItemSelectionState[]): ItemSelectionState[] =>
  selection.map(item => ({
    ...item,
    selected: false
  }));

const applySelect = (path: number[], propagate: boolean, selection: ItemSelectionState[]): ItemSelectionState[] => {

  return selection.map(item => path.includes(item.path[item.path.length - 1]) ||
    (propagate && deepEquals(path, item.path.slice(0, path.length)))
    ? {
      ...item,
      selected: true
    }
    : item);
};

const applyDeselect = (path: number[], propagate: boolean, selection: ItemSelectionState[]): ItemSelectionState[] => {

  return selection.map(item => deepEquals(path, item.path) ||
    (propagate && deepEquals(path, item.path.slice(0, path.length)))
    ? {
      ...item,
      selected: false
    }
    : item);
};

const actionFactory = {
  [linkCodesAction.selectAll]: applySelectAll,
  [linkCodesAction.deselectAll]: applyDeselectAll,
  [linkCodesAction.select]: applySelect,
  [linkCodesAction.deselect]: applyDeselect
} as const;

export const mapActionsToSelection = (
  selectionState: ItemSelectionState[],
  listActions: ListAction[]
) => {

  return (listActions ?? [])
    .reduce(
      (selection, action) => actionFactory[action.type]?.(action.path, action.propagate, selection) ?? selection,
      selectionState ?? [] as ItemSelectionState[]
    );
};
